import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { SubCategory } from '../../../types/category.types';

export interface Category {
  id: string;
  name: string;
  image: string;
  TypesItems: string;
  vendorCount: number;
  subCategories?: SubCategory[];
}

interface CategoriesTableProps {
  categories: Category[];
  onReorder: (reorderedCategories: Category[]) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  onCategoryClick?: (category: Category) => void;
}

// Render functions for table cells
const renderDragHandle = (isDragging: boolean, attributes: any, listeners: any) => (
  <Box
    {...attributes}
    {...listeners}
    sx={{
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'grab',
      '&:active': {
        cursor: 'grabbing',
      },
    }}
  >
    <DragIndicatorIcon sx={{ width: '24px', height: '24px', color: 'var(--palette-light-text-secondary-value-hex)' }} />
  </Box>
);

const renderCategoryImage = (image: string, name: string) => (
  <Box sx={{ 
    width: '109px', 
    height: '109px', 
    borderRadius: 'var(--palette-light-4-value)', 
    overflow: 'hidden', 
    backgroundColor: 'var(--palette-light-background-secondary-value-hex)', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    position: 'relative'
  }}>
    <Box
      component="img"
      src={image}
      alt={name}
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onLoad={() => console.log('✅ Image loaded:', image)}
      onError={(e: any) => {
        console.error('❌ Image failed to load:', image);
        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTA5IiBoZWlnaHQ9IjEwOSIgdmlld0JveD0iMCAwIDEwOSAxMDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDkiIGhlaWdodD0iMTA5IiBmaWxsPSIjRjRGNEY0Ii8+CjxwYXRoIGQ9Ik01NC41IDMwQzQxLjUgMzAgMzEgNDAuNSAzMSA1My41QzMxIDY2LjUgNDEuNSA3NyA1NC41IDc3QzY3LjUgNzcgNzggNjYuNSA3OCA1My41Qzc4IDQwLjUgNjcuNSAzMCA1NC41IDMwWiIgc3Ryb2tlPSIjQzRDNEM0IiBzdHJva2Utd2lkdGg9IjIiLz4KPC9zdmc+Cg==';
        e.target.style.objectFit = 'contain';
      }}
    />
    {/* Category Name Overlay */}
    <Box sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 70%, transparent 100%)',
      padding: '8px 12px',
      display: 'flex',
      alignItems: 'flex-end',
      minHeight: '40px'
    }}>
      <Typography sx={{ 
        fontSize: '14px',
        fontWeight: 600,
        color: '#FFFFFF',
        lineHeight: '1.2',
        wordBreak: 'break-word',
        width: '100%'
      }}>
        {name}
      </Typography>
    </Box>
  </Box>
);

const renderActions = (onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void, id: string) => (
  <Box sx={{ width: '56px', height: '56px' }}>
    <IconButton
      onClick={(e) => onMenuOpen(e, id)}
      sx={{
        width: '56px',
        height: '56px',
        borderRadius: 'var(--palette-light-4-value)',
        color: 'var(--palette-light-brand-primary-value-hex)',
        '&:hover': {
          backgroundColor: 'rgba(103, 49, 79, 0.04)',
        },
      }}
    >
      <MoreHorizIcon sx={{ fontSize: '24px' }} />
    </IconButton>
  </Box>
);

// Sortable Row Component for Categories
const SortableCategoryRow: React.FC<{
  category: Category;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  onCategoryClick?: (category: Category) => void;
}> = ({ category, onMenuOpen, onCategoryClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--palette-light-4-value)',
        padding: '11px 0',
        borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
        backgroundColor: 'var(--palette-light-background-primary-value-hex)',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {renderDragHandle(isDragging, attributes, listeners)}
      <Box
        onClick={() => onCategoryClick?.(category)}
        sx={{
          cursor: onCategoryClick ? 'pointer' : 'default',
          flexShrink: 0,
        }}
      >
        {renderCategoryImage(category.image, category.name)}
      </Box>
      <Box sx={{ flex: 1 }}>
        {category.subCategories && category.subCategories.length > 0 ? (
          category.subCategories.map((subCat) => (
            <Box key={subCat.name} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Subcategory Image */}
              {subCat.iconURL && (
                <Box
                  component="img"
                  src={subCat.iconURL}
                  alt={subCat.name}
                  sx={{ 
                    width: '24px', 
                    height: '24px', 
                    borderRadius: '4px', 
                    objectFit: 'cover',
                    border: '1px solid #eee',
                    flexShrink: 0
                  }}
                />
              )}
              
              <Box sx={{ flex: 1 }}>
                <Typography sx={{fontSize: 'var(--palette-light-font-size-default-value)',color: 'var(--palette-light-text-primary-value-hex)'}}>
                  {subCat.name}: ({subCat.types.map((t, idx) => (
                    <span key={t.name}>
                      {t.iconURL && (
                        <Box
                          component="img"
                          src={t.iconURL}
                          alt={t.name}
                          sx={{ 
                            width: '16px', 
                            height: '16px', 
                            borderRadius: '2px', 
                            objectFit: 'cover', 
                            mr: 0.5,
                            ml: idx > 0 ? 0.5 : 0,
                            verticalAlign: 'middle',
                            border: '1px solid #eee'
                          }}
                        />
                      )}
                      {t.name}
                      {idx < subCat.types.length - 1 && ', '}
                    </span>
                  ))})
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {subCat.types.flatMap(t => t.items).join(', ')}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography
            sx={{
              fontSize: 'var(--palette-light-font-size-default-value)',
              color: 'var(--palette-light-text-primary-value-hex)',
            }}
          >
            No sub-categories
          </Typography>
        )}
      </Box>
      <Box sx={{ width: '80px', display: 'flex', justifyContent: 'flex-end' }}>
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', opacity: 0.86 }}>
          {category.vendorCount}
        </Typography>
      </Box>
      {renderActions(onMenuOpen, category.id)}
    </Box>
  );
};

const CategoriesTable: React.FC<CategoriesTableProps> = ({
  categories,
  onReorder,
  onMenuOpen,
  onCategoryClick,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((item) => item.id === active.id);
      const newIndex = categories.findIndex((item) => item.id === over.id);
      
      const reordered = [...categories];
      const [removed] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, removed);
      
      onReorder(reordered);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* Table Header */}
      <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)', padding: 'var(--palette-light-3-value) 0', borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)' }}>
        <Box sx={{ width: '24px' }} />
        <Box sx={{ width: '110px' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Category
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Types
          </Typography>
        </Box>
        <Box sx={{ width: '80px', display: 'flex', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Vendors
          </Typography>
        </Box>
        <Box sx={{ width: '56px' }} />
      </Box>

      {/* Table Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={categories.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {categories.map((category) => (
              <SortableCategoryRow
                key={category.id}
                category={category}
                onMenuOpen={onMenuOpen}
                onCategoryClick={onCategoryClick}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  );
};

export default CategoriesTable;

